import React, { useState, useEffect } from 'react';
import { NativeChessBoard } from './NativeChessBoard';
import { PlayerInfoCard } from './PlayerInfoCard';
import { GameControls } from './GameControls';
import { BotChatPanel } from './BotChatPanel';
import { GameStatusBar } from './GameStatusBar';
import { useLocalClock } from '@/hooks/useLocalClock';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAudioFeedback } from '@/hooks/useAudioFeedback';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { Button } from './ui/button';
import { Position } from '@/lib/chess-engine';
import { 
  HatchGameState, 
  createInitialHatchGameState, 
  validateHatchMove,
  isPieceEvolved,
  getEvolutionHistory,
  getEvolutionChainDescription
} from '@/lib/hatch-evolution-engine';
import { getEngineMove } from '@/lib/unified-engine';
import { getGameStatus } from '@/lib/chess-engine';
import { Card, CardContent } from './ui/card';
import { Sparkles, Info } from 'lucide-react';
import { toast } from 'sonner';

interface HatchEvolutionBoardProps {
  gameMode: 'hatch';
  opponent: {
    name: string;
    isBot: boolean;
    elo?: number;
  };
  timeControl?: string;
  onMove?: (from: Position, to: Position) => void;
  onResign?: () => void;
  onTakeback?: () => void;
  onBackToMenu?: () => void;
}

export function HatchEvolutionBoard({
  gameMode,
  opponent,
  timeControl,
  onMove,
  onResign,
  onTakeback,
  onBackToMenu
}: HatchEvolutionBoardProps) {
  const [gameState, setGameState] = useState<HatchGameState>(createInitialHatchGameState());
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [validMoves, setValidMoves] = useState<Position[]>([]);
  const [gameFinished, setGameFinished] = useState(false);
  const [botThinking, setBotThinking] = useState(false);
  const [showEvolutionInfo, setShowEvolutionInfo] = useState(false);

  const { profile } = useUserProfile();
  const { playMoveSound, playGameEventSound } = useAudioFeedback();
  const { triggerHaptic } = useHapticFeedback();
  
  const {
    whiteTimeMs,
    blackTimeMs
  } = useLocalClock('10+0', gameState.sideToMove, 'ongoing');

  // Handle bot moves
  useEffect(() => {
    if (gameState.sideToMove === 'black' && opponent.isBot && !gameFinished && !botThinking) {
      setBotThinking(true);
      
      const makeMove = async () => {
        try {
          const result = await getEngineMove(gameState, opponent.elo || 1200);
          
          if (result.move && result.move.from && result.move.to) {
            const moveResult = validateHatchMove(gameState, result.move.from, result.move.to);
            
            if (moveResult.isLegal && moveResult.newState) {
              setGameState(moveResult.newState as HatchGameState);
              playMoveSound();
              
              // Play evolution sound if evolution occurred
              if (moveResult.evolutionRecord) {
                setTimeout(() => {
                  playGameEventSound('promotion'); // Use promotion sound for evolution
                  toast.success(
                    `🧬 ${opponent.name}'s ${moveResult.evolutionRecord!.originalPiece} evolved into ${moveResult.evolutionRecord!.evolvedTo}!`
                  );
                }, 300);
              }
              
              onMove?.(result.move.from, result.move.to);
            }
          }
        } catch (error) {
          console.error('Bot move error:', error);
        } finally {
          setBotThinking(false);
        }
      };
      
      // Add slight delay for better UX
      setTimeout(makeMove, 500);
    }
  }, [gameState.sideToMove, opponent.isBot, gameFinished, botThinking]);

  const handleSquareClick = (position: Position) => {
    triggerHaptic('selection');
    const [row, col] = position;
    const piece = gameState.board[row][col];

    if (selectedSquare) {
      const [selectedRow, selectedCol] = selectedSquare;
      
      if (selectedRow === row && selectedCol === col) {
        // Deselect if clicking same square
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }

      // Try to make a move
      const moveResult = validateHatchMove(gameState, selectedSquare, position);
      
      if (moveResult.isLegal && moveResult.newState) {
        setGameState(moveResult.newState as HatchGameState);
        setSelectedSquare(null);
        setValidMoves([]);
        playMoveSound();
        
        // Handle evolution animation and notification
        if (moveResult.evolutionRecord) {
          setTimeout(() => {
            playGameEventSound('promotion'); // Use promotion sound for evolution
            toast.success(
              `🧬 Evolution! Your ${moveResult.evolutionRecord!.originalPiece} evolved into ${moveResult.evolutionRecord!.evolvedTo}!`,
              { duration: 4000 }
            );
          }, 300);
        }
        
        onMove?.(selectedSquare, position);
        return;
      }
      
      // If move wasn't legal, try selecting the new piece
      setSelectedSquare(null);
      setValidMoves([]);
    }

    // Select piece if it belongs to current player
    if (piece && piece.color === gameState.sideToMove) {
      setSelectedSquare(position);
      // For now, set empty valid moves - we'll implement proper move generation later
      setValidMoves([]);
    }
  };

  const handleResign = () => {
    setGameFinished(true);
    onResign?.();
  };

  const handleTakeback = () => {
    onTakeback?.();
  };

  const gameStatus = getGameStatus(gameState);
  const isPlayerTurn = gameState.sideToMove === 'white';

  // Highlight evolved pieces
  const highlightedSquares: Position[] = [];
  gameState.evolutionHistory.forEach(evolution => {
    highlightedSquares.push(evolution.position);
  });

  return (
    <div className="w-full max-w-7xl mx-auto p-4 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 dark:from-violet-950 dark:via-fuchsia-900/20 dark:to-cyan-950 min-h-screen relative">
      {/* Floating Particles - only in dark mode */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden hidden dark:block">
        <div className="absolute top-10 left-10 text-cyan-300 text-xl animate-float-particle">✨</div>
        <div className="absolute top-20 right-20 text-pink-400 text-2xl animate-float-particle" style={{ animationDelay: '1.5s' }}>⭐</div>
        <div className="absolute bottom-20 left-1/4 text-yellow-300 text-xl animate-float-particle" style={{ animationDelay: '3s' }}>💫</div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] xl:grid-cols-[320px_1fr_320px] gap-6 relative z-10">
        {/* Left Panel - Game Info */}
        <div className="space-y-4">
          <PlayerInfoCard
            name={opponent.name}
            isBot={opponent.isBot}
            elo={opponent.elo}
            timeMs={blackTimeMs}
            isActivePlayer={gameState.sideToMove === 'black'}
            color="black"
          />
          
          <Card className="border-pink-500/30 bg-gradient-to-br from-violet-900/40 via-fuchsia-800/30 to-cyan-900/40 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                <span className="font-semibold text-cyan-100">Hatch Evolution</span>
              </div>
              <p className="text-sm text-pink-200 mb-3">
                Pieces evolve when they capture!
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEvolutionInfo(!showEvolutionInfo)}
                className="w-full border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10 hover:text-cyan-100"
              >
                <Info className="w-4 h-4 mr-2" />
                {showEvolutionInfo ? 'Hide' : 'Show'} Rules
              </Button>
              
              {showEvolutionInfo && (
                <div className="mt-3 p-3 bg-gradient-to-br from-violet-950/50 to-cyan-950/50 rounded-lg border border-pink-500/20">
                  <pre className="text-xs whitespace-pre-wrap font-mono text-cyan-100">
                    {getEvolutionChainDescription()}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>

          {gameState.evolutionHistory.length > 0 && (
            <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-900/40 via-purple-900/30 to-pink-900/40 backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]" />
                  <span className="font-semibold text-yellow-100">Evolution History</span>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {gameState.evolutionHistory.slice(-5).map((evolution, idx) => (
                    <div key={idx} className="text-xs p-2 bg-gradient-to-r from-pink-950/50 to-cyan-950/50 rounded border border-pink-500/20 text-cyan-100">
                      Move {evolution.moveNumber}: {evolution.originalPiece} ➜ {evolution.evolvedTo}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Center - Chess Board */}
        <div className="space-y-4">
          <div className="text-center p-3 bg-gradient-to-r from-violet-900/50 via-fuchsia-800/50 to-cyan-900/50 rounded-lg border border-pink-500/30 backdrop-blur-sm">
            <span className="font-semibold bg-gradient-to-r from-cyan-200 via-pink-300 to-purple-300 bg-clip-text text-transparent">
              {gameStatus.status === 'ongoing' && (isPlayerTurn ? "✨ Your turn" : `⭐ ${opponent.name}'s turn`)}
              {gameStatus.status === 'check' && "⚡ Check!"}
              {gameStatus.status === 'checkmate' && `👑 Checkmate! ${gameStatus.winner} wins`}
              {gameStatus.status === 'stalemate' && "🤝 Stalemate"}
              {botThinking && "🧠 Bot is thinking..."}
            </span>
          </div>
          
          <NativeChessBoard
            gameState={gameState}
            onSquareClick={handleSquareClick}
            highlightedSquares={highlightedSquares}
            selectedSquare={selectedSquare}
            flipped={false}
            enableSwipeGestures={true}
            enableHapticFeedback={true}
          />
          
          <GameControls
            onResign={handleResign}
            onTakeback={handleTakeback}
            canTakeback={gameState.moveHistory.length > 0}
          />
        </div>

        {/* Right Panel */}
        <div className="space-y-4">
          <PlayerInfoCard
            name={profile?.display_name || 'You'}
            isBot={false}
            timeMs={whiteTimeMs}
            isActivePlayer={gameState.sideToMove === 'white'}
            color="white"
          />

          {opponent.isBot && (
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <h3 className="font-semibold mb-2">{opponent.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {botThinking ? "Thinking..." : "Ready to play!"}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {onBackToMenu && (
        <div className="mt-6 text-center">
          <Button onClick={onBackToMenu} variant="outline">
            Back to Menu
          </Button>
        </div>
      )}
    </div>
  );
}